import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit2, Trash2, Store, MapPin, Phone, Landmark, KeyRound, Copy, Mail } from "lucide-react";
import Pager from "@/pages/admin/Pager";

const emptyBank = { account_holder: "", account_number: "", ifsc: "", bank_name: "" };
const empty = { name: "", location: "", contact: "", phone: "", email: "", pin: "", bank_details: { ...emptyBank }, active: true };

export default function ShopsTab() {
  const [shops, setShops] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/admin/shops", { params: { limit, skip } }).then((r) => {
    setShops(r.data.items || []);
    setTotal(r.data.total || 0);
  }), [limit, skip]);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBank = (k, v) => setForm((f) => ({ ...f, bank_details: { ...(f.bank_details || {}), [k]: v } }));

  const startNew = () => { setEdit(null); setForm({ ...empty, bank_details: { ...emptyBank } }); setOpen(true); };
  const startEdit = (s) => {
    setEdit(s);
    setForm({
      name: s.name || "",
      location: s.location || "",
      contact: s.contact || "",
      phone: s.phone || "",
      email: s.email || "",
      pin: "",  // PIN never surfaced; leave blank to keep existing
      bank_details: s.bank_details || { ...emptyBank },
      active: s.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    const bank = form.bank_details || {};
    const hasAnyBank = Object.values(bank).some((v) => String(v || "").trim());
    const bankReady = hasAnyBank ? bank : null;
    const payload = {
      name: form.name.trim(),
      location: form.location?.trim() || "",
      contact: form.contact?.trim() || "",
      phone: form.phone?.trim() || "",
      email: form.email?.trim() || "",
      bank_details: bankReady,
      active: !!form.active,
    };
    if (form.pin?.trim()) payload.pin = form.pin.trim();
    if (!payload.name) return toast.error("Shop name is required");
    if (!payload.phone) return toast.error("Shop phone number is required (used for login)");
    try {
      if (edit) await api.put(`/admin/shops/${edit.id}`, payload);
      else await api.post("/admin/shops", payload);
      toast.success(edit ? "Shop updated" : "Shop created — the shop sets their own email and PIN on first sign-in.");
      setOpen(false); setEdit(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/shops/${s.id}`);
      toast.success("Shop deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyId = (id) => {
    navigator.clipboard?.writeText(id);
    toast.success(`Shop ID copied: ${id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button data-testid="btn-add-shop" onClick={startNew} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Add tie-up shop
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {shops.map((s) => (
          <div key={s.id} data-testid={`shop-card-${s.id}`} className="velo-card p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Store className="w-4 h-4 text-pink-400" strokeWidth={2} />
                  <div className="text-white font-medium truncate">{s.name}</div>
                </div>
                <button onClick={() => copyId(s.id)} className="text-xs font-mono text-pink-400 hover:text-pink-300 flex items-center gap-1">
                  {s.id} <Copy className="w-3 h-3" strokeWidth={2} />
                </button>
              </div>
              <div className="flex gap-1">
                <Badge className={`border text-[10px] ${s.active ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-slate-500/20 text-slate-400 border-slate-500/30"}`}>
                  {s.active ? "Active" : "Inactive"}
                </Badge>
                <Button size="sm" variant="ghost" onClick={() => startEdit(s)} className="text-slate-400 hover:text-white h-8 w-8 p-0"><Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(s)} className="text-slate-400 hover:text-red-400 h-8 w-8 p-0"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
              </div>
            </div>
            <div className="mt-3 space-y-1 text-xs text-slate-400">
              {s.phone ? <div className="flex items-center gap-1.5 text-white/70"><Phone className="w-3 h-3" /><span className="font-mono">{s.phone}</span> <span className="text-slate-500">· login</span></div> : <div className="text-amber-400 text-[11px]">No shop phone set · login disabled</div>}
              {s.access_code ? <div className="flex items-center gap-1.5"><KeyRound className="w-3 h-3" /><span className="font-mono">{s.access_code}</span> <span className="text-slate-500">· first-login code</span></div> : null}
              {s.email ? <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{s.email}{s.email_verified ? " ✓" : ""}</div> : <div className="text-[11px] text-slate-500">Email not set — shop will add it on first sign-in</div>}
              {s.location ? <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{s.location}</div> : null}
              {s.contact ? <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{s.contact} <span className="text-slate-500">· manager</span></div> : null}
              {s.bank_details?.account_number ? (
                <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-white/5">
                  <Landmark className="w-3 h-3 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-white truncate">{s.bank_details.account_holder}</div>
                    <div className="font-mono text-slate-500 truncate">{s.bank_details.bank_name} · {s.bank_details.account_number} · {s.bank_details.ifsc}</div>
                  </div>
                </div>
              ) : <div className="text-amber-400 text-[11px] mt-2">Bank details not set</div>}
            </div>
          </div>
        ))}
        {shops.length === 0 && (
          <div className="col-span-full velo-card p-10 text-center">
            <Store className="w-8 h-8 text-slate-600 mx-auto mb-3" strokeWidth={1.5} />
            <div className="text-slate-400 text-sm">No tie-up shops yet. Add your first one to enable shop redemptions.</div>
          </div>
        )}
      </div>

      {total > 0 && (
        <div className="velo-card overflow-hidden">
          <Pager
            total={total}
            limit={limit}
            skip={skip}
            testidPrefix="pager-shops"
            onChange={({ limit: l, skip: s }) => { setLimit(l); setSkip(s); }}
          />
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle>{edit ? `Edit shop · ${edit.id}` : "New tie-up shop"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="col-span-1 sm:col-span-2"><Label className="text-slate-300">Shop name</Label><Input data-testid="input-shop-name" value={form.name} onChange={(e) => setField("name", e.target.value)} className="velo-input mt-2" /></div>
            <div><Label className="text-slate-300 flex items-center gap-2"><Phone className="w-3.5 h-3.5" strokeWidth={2} /> Shop phone <span className="text-[10px] text-pink-400 uppercase tracking-wider">login</span></Label><Input data-testid="input-shop-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value.replace(/[^0-9+]/g, ""))} placeholder="e.g. 9876543210" className="velo-input mt-2 font-mono" /></div>
            <div><Label className="text-slate-300 flex items-center gap-2"><Mail className="w-3.5 h-3.5" strokeWidth={2} /> Shop email <span className="text-[10px] text-slate-500 uppercase tracking-wider">optional — shop sets on first login</span></Label><Input data-testid="input-shop-email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="Optional — leave blank if unknown" className="velo-input mt-2" /></div>
            <div><Label className="text-slate-300">Location</Label><Input data-testid="input-shop-location" value={form.location} onChange={(e) => setField("location", e.target.value)} placeholder="e.g. Bandra West, Mumbai" className="velo-input mt-2" /></div>
            <div><Label className="text-slate-300">Manager contact</Label><Input data-testid="input-shop-contact" value={form.contact} onChange={(e) => setField("contact", e.target.value)} placeholder="Manager phone (optional)" className="velo-input mt-2" /></div>
            <div className="col-span-1 sm:col-span-2">
              <Label className="text-slate-300 flex items-center gap-2"><KeyRound className="w-3.5 h-3.5" strokeWidth={2} /> PIN (4 digits) <span className="text-[10px] text-slate-500 uppercase tracking-wider">{edit ? "— leave blank to keep existing" : "— optional; shop will set it on first sign-in"}</span></Label>
              <Input data-testid="input-shop-pin" type="password" value={form.pin} onChange={(e) => setField("pin", e.target.value.replace(/[^0-9]/g, ""))} className="velo-input mt-2 font-mono tracking-widest" maxLength={4} placeholder={edit ? "•••• (unchanged)" : "Leave blank — shop sets on first sign-in"} />
            </div>
            <div className="col-span-1 sm:col-span-2 pt-3 border-t border-white/5">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5"><Landmark className="w-3 h-3" /> Bank details (payouts sent here)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-slate-300 text-xs">Account holder</Label><Input data-testid="input-bank-holder" value={form.bank_details?.account_holder || ""} onChange={(e) => setBank("account_holder", e.target.value)} className="velo-input mt-2" /></div>
                <div><Label className="text-slate-300 text-xs">Bank name</Label><Input data-testid="input-bank-name" value={form.bank_details?.bank_name || ""} onChange={(e) => setBank("bank_name", e.target.value)} className="velo-input mt-2" /></div>
                <div><Label className="text-slate-300 text-xs">Account number</Label><Input data-testid="input-bank-account" value={form.bank_details?.account_number || ""} onChange={(e) => setBank("account_number", e.target.value)} className="velo-input mt-2 font-mono" /></div>
                <div><Label className="text-slate-300 text-xs">IFSC</Label><Input data-testid="input-bank-ifsc" value={form.bank_details?.ifsc || ""} onChange={(e) => setBank("ifsc", e.target.value.toUpperCase())} className="velo-input mt-2 font-mono" /></div>
              </div>
            </div>
            <div className="col-span-1 sm:col-span-2 flex items-center gap-2">
              <input data-testid="input-shop-active" type="checkbox" checked={!!form.active} onChange={(e) => setField("active", e.target.checked)} className="w-4 h-4 accent-pink-500" />
              <span className="text-sm text-slate-300">Active (can accept scans)</span>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="btn-save-shop" onClick={save} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
